import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { VIP_CLIENTS } from '../constants/vipClients';
import { API_BASE } from '../config';
import '../styles/screen.css';

// Elapsed time as "Xh Ym", the same format the pickeo column shows.
const formatElapsed = (since) => {
  const diffInMs = Date.now() - new Date(since);
  const hours = Math.floor(diffInMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
};

// The checker verifies cart by cart. A side counts as revised once every
// cart of that side (Abarrotes carts / Frío baskets) has been checked.
// Sides with no carts on the order get no entry.
const REVIEW_SIDES = [
  { label: 'Abarrotes', prefix: 'CART' },
  { label: 'Frío', prefix: 'BASKET' },
];

const reviewSides = (carts = []) =>
  REVIEW_SIDES.flatMap(({ label, prefix }) => {
    const sideCarts = carts.filter(cart => cart.code.startsWith(prefix));
    if (sideCarts.length === 0) return [];
    return [{ label, ready: sideCarts.every(cart => cart.checked) }];
  });

function Screen() {
  const { logout, token } = useAuth();
  const [poData, setPoData] = useState([]);
  const navigate = useNavigate(); // Inicializa useNavigate
  const previousPendingPOs = useRef(new Set()); // Store previously seen POs
  const bellSound = useRef(new Audio('/sounds/bell.mp3')); // Load bell sound
  const playBellSound = () => {
    bellSound.current.play().catch(error => console.error("Error playing sound:", error));
  };

  // Helper function to check if a client is VIP
  const isVIPClient = (clientName) => {
    return VIP_CLIENTS.some(vipClient =>
      vipClient.toLowerCase().trim() === clientName.toLowerCase().trim()
    );
  };

  // Helper function to sort pending clients with VIP priority
  const sortPendingClients = (clients) => {
    return clients.sort((a, b) => {
      const aIsVIP = isVIPClient(a.client);
      const bIsVIP = isVIPClient(b.client);

      // VIP clients go first
      if (aIsVIP && !bIsVIP) return -1;
      if (!aIsVIP && bIsVIP) return 1;

      // If both are VIP or both are not VIP, sort by PO date (oldest first)
      const aDate = new Date(a.po_date);
      const bDate = new Date(b.po_date);
      return aDate - bDate;
    });
  };

  const fetchPoData = useCallback(async () => {
    if (!token) {
      console.error('No token available');
      return;
    }

    try {
      // New endpoint - comment this out to test with old endpoint
      const response = await fetch(`${API_BASE}/get-screen`, {
        method: 'GET',
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      // Old endpoint - uncomment this to test with previous endpoint
      // const response = await fetch(`${API_BASE}/get-all-po`, {
      //   method: 'GET',
      //   headers: {
      //     Authorization: `Token ${token}`,
      //   },
      // });

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setPoData(data);
          console.log('Data received:', data);

          // Extract the list of current pending PO numbers
          const newPendingPOs = new Set(data.filter(po => po.status === 'pending').map(po => po.po_number));

          // Compare with the previous list to detect truly new POs
          let newPOArrived = false;
          newPendingPOs.forEach(poNumber => {
              if (!previousPendingPOs.current.has(poNumber)) {
                  newPOArrived = true; // Found a new PO
              }
          });

          // If there's a truly new PO, play the bell sound
          if (newPOArrived) {
              playBellSound();
          }

          // Update the stored pending POs
          previousPendingPOs.current = newPendingPOs;
        } else {
          console.error('Data received is not an array:', data);
          setPoData([]);
        }
      } else {
        console.error('Failed to fetch PO data', response.status, response.statusText);
        setPoData([]);
      }
    } catch (error) {
      console.error('Error:', error);
      setPoData([]);
    }
  }, [token]);

  useEffect(() => {
    fetchPoData();

    const interval = setInterval(fetchPoData, 60000); // Update every 1 minute
    return () => clearInterval(interval);
  }, [fetchPoData]);

  const handleLogout = () => {
    logout();
  };

  // Filter the PO data by status and apply VIP sorting to pending clients
  const pendingClients = sortPendingClients(poData.filter(po => po.status === 'pending'));
  // POs waiting on a quantity decision are pinned above the picked orders so
  // approvers see them first. They also keep their card in "Pendientes de
  // pickeo" — the picker still needs to find the order in their own column.
  const awaitingModification = poData
    .filter(po => po.status !== 'picked' && po.pending_quantity_requests > 0)
    .sort((a, b) => new Date(a.po_date) - new Date(b.po_date));

  const pickedClients = [
    ...awaitingModification,
    ...poData.filter(po => po.status === 'picked'),
  ];
  const checkedClients = poData
    .filter(po => po.status === 'checked')
    .sort((a, b) => new Date(a.po_date) - new Date(b.po_date)); // Sort from oldest to newest
  const paidClients = poData.filter(po => po.status === 'paid');

  // Helper function to format cart type
  const formatCartType = (code) => {
    if (code.startsWith('CART-')) {
      const number = code.split('-')[1];
      return `Abarrote Nº ${number}`;
    }
    if (code.startsWith('BASKET-')) {
      const number = code.split('-')[1];
      return `Frío nº ${number}`;
    }
    return code;
  };

  // Calculate days elapsed since PO date
  const calculateDaysElapsed = (poDate) => {
    if (!poDate) return 0;
    const date = new Date(poDate);
    const now = new Date();
    const diffInMs = now - date;
    return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  };

  const PoList = ({ title, poList, showElapsedTime = false, showReviewInfo = false }) => {
    return (
      <div className="screen-column">
        <h2>
          {title}
          <div className="count-box">{poList.length}</div>
        </h2>
        {poList.length > 0 ? (
          poList.map((po, index) => {
            // Calculate elapsed time if needed
            let elapsedTime = "";
            if (showElapsedTime && po.po_date) {
              const poDate = new Date(po.po_date);
              const now = new Date();
              const diffInMs = now - poDate;
              const hours = Math.floor(diffInMs / (1000 * 60 * 60));
              const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
              elapsedTime = `${hours}h ${minutes}m`;
            }

            // Calculate days for checked orders
            const daysElapsed = calculateDaysElapsed(po.po_date);
            const showDaysAlert = title === "Checkeados" && daysElapsed >= 7;
            const showTimeBadge = title === "Checkeados";

            // Check if client is VIP for styling
            const isVIP = isVIPClient(po.client);

            return (
              <div key={index} className={`client-details ${isVIP ? 'vip-client' : ''}`}>
                <div className="client-header">
                  <strong>{po.po_number}</strong> {po.client}
                  {isVIP && <span className="vip-badge">VIP</span>}
                </div>
                <div className="packing-info" style={{ display: 'flex', gap: '8px' }}>
                  {po.packing && (
                    <span className="packing-badge">
                      📦 EMBALAR
                    </span>
                  )}
                  {po.pending_quantity_requests > 0 && (
                    <span className="modification-badge">
                      ✏️ MODIFICACIÓN
                    </span>
                  )}
                  {showTimeBadge && (
                    <span className="packing-badge" style={{ backgroundColor: showDaysAlert ? '#ff4444' : '#4CAF50', color: 'white' }}>
                      Tiempo: {daysElapsed} días
                    </span>
                  )}
                </div>
                {po.address && <div className="client-address">{po.address}</div>}
                {po.es_empleado && po.note?.trim() && (
                  <div className="screen-order-note">
                    <strong>Nota del pedido:</strong> {po.note}
                  </div>
                )}

                {/* Show elapsed time if enabled */}
                {showElapsedTime && elapsedTime && (
                  <div className="time-elapsed">
                    <strong>Tiempo:</strong> {elapsedTime}
                  </div>
                )}

                {/* Revisión column: time waiting for the checker, and which
                    side is already revised. Orders pinned here while a
                    modification is pending are not picked yet, so they keep
                    the order-date timer and get no side badges. */}
                {showReviewInfo && (
                  po.status === 'picked' && po.picked_at ? (
                    <div className="time-elapsed">
                      <strong>En revisión:</strong> {formatElapsed(po.picked_at)}
                    </div>
                  ) : (
                    <div className="time-elapsed">
                      <strong>Tiempo:</strong> {formatElapsed(po.po_date)}
                    </div>
                  )
                )}
                {showReviewInfo && po.status === 'picked' && (
                  <div className="review-sides">
                    {reviewSides(po.carts).map(side => (
                      <span
                        key={side.label}
                        className={`review-side-badge ${side.ready ? 'review-side-ready' : 'review-side-waiting'}`}
                      >
                        {`${side.ready ? '✓' : '⏳'} ${side.label} ${side.ready ? 'revisado' : 'pendiente'}`}
                      </span>
                    ))}
                  </div>
                )}

                {po.carts && po.carts.length > 0 && (
                  <ul>
                    {po.carts.map((cart, cartIndex) => {
                      const isCart = cart.code.startsWith("CART");
                      const isBasket = cart.code.startsWith("BASKET");

                      return (
                        <li key={cartIndex}>
                          {formatCartType(cart.code)}
                          {isCart && po.item_responsable.length > 0 && (
                            <span className={`responsable-item ${po.status === 'pending' && po.item_status ? 'status-ready' : ''}`}>
                              <strong> - </strong>{" "}
                              {po.item_responsable.map(responsable => responsable.name).join(", ")}
                            </span>
                          )}
                          {isBasket && po.frozen_responsable.length > 0 && (
                            <span className={`responsable-item ${po.status === 'pending' && po.frozen_status ? 'status-ready' : ''}`}>
                              <strong> - </strong>{" "}
                              {po.frozen_responsable.map(responsable => responsable.name).join(", ")}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })
        ) : (
          <p>No hay {title.toLowerCase()}</p>
        )}
      </div>
    );
  };

  return (
    <div className='screen-margin'>
                              <button className="adminproductos-back-button" onClick={() => navigate(`/init`)}>
                &#8592;
            </button>
  <h1 className="page-title">Pedidos</h1>
  <div className="screen-container">
  <button className="screen-logout-button" onClick={handleLogout}>Cerrar Sesión</button>

  {/* Pending Clients (with elapsed time) */}
  <PoList title="Pendientes de pickeo" poList={pendingClients} showElapsedTime={true} />

  {/* Picked Clients */}
  <PoList title="Pendientes de revisión" poList={pickedClients} showReviewInfo={true} />

  {/* Checked Clients */}
  <PoList title="Checkeados" poList={checkedClients} />

  {/* Paid Clients */}
  <PoList title="Pagados" poList={paidClients} />
</div>
  {/* <div className="screen-container">
    <button className="screen-logout-button" onClick={handleLogout}>Cerrar Sesión</button>

    <div className="screen-column">
  <h2>
    Pendientes de pickeo
    <div className="count-box">{pendingClients.length}</div>
  </h2>
  {pendingClients.length > 0 ? (
    pendingClients.map((po, index) => {
      // Calcular tiempo transcurrido
      const poDate = new Date(po.po_date);
      const now = new Date();
      const diffInMs = now - poDate; // Diferencia en milisegundos
      const hours = Math.floor(diffInMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

      return (
        <div key={index} className="client-details">
          <div>
            <strong>{po.po_number}</strong>. {po.client}
          </div>
          {po.address && <div className="client-address">{po.address}</div>}
          <div className="time-elapsed">
            <strong>Tiempo:</strong> {`${hours}h ${minutes}m`}
          </div>

          {po.carts && po.carts.length > 0 && (
            <div>
              <h4>Abarrotes en uso:</h4>
              <ul>
                {po.carts
                  .filter(cart => cart.status === 'in_use')
                  .map((cart, cartIndex) => (
                    <li key={cartIndex}>{formatCartType(cart.code)}</li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      );
    })
  ) : (
    <p>No hay pendientes de pickeo</p>
  )}
</div>


    <div className="screen-column">
      <h2>
        Pendientes de revisión
        <div className="count-box">{pickedClients.length}</div>
      </h2>
      {pickedClients.length > 0 ? (
        pickedClients.map((po, index) => (
          <div key={index} className="client-details">
            <div>
              <strong>{po.po_number}</strong>. {po.client}
            </div>
            {po.address && <div className="client-address">{po.address}</div>}
          </div>
        ))
      ) : (
        <p>No hay pendientes de revisión</p>
      )}
    </div>

    <div className="screen-column">
      <h2>
        Checkeados
        <div className="count-box">{checkedClients.length}</div>
      </h2>
      {checkedClients.length > 0 ? (
        checkedClients.map((po, index) => (
          <div key={index} className="client-details">
            <div>
              <strong>{po.po_number}</strong>. {po.client}
            </div>
            {po.address && <div className="client-address">{po.address}</div>}
            {po.carts && po.carts.length > 0 && (
              <ul>
                {po.carts.map((cart, cartIndex) => (
                  <li key={cartIndex}>{formatCartType(cart.code)}</li>
                ))}
              </ul>
            )}
          </div>
        ))
      ) : (
        <p>No hay pendientes de checkeo</p>
      )}
    </div>

    <div className="screen-column">
      <h2>
        Pagados
        <div className="count-box">{paidClients.length}</div>
      </h2>
      {paidClients.length > 0 ? (
        paidClients.map((po, index) => (
          <div key={index} className="client-details">
            <div>
              <strong>{po.po_number}</strong>. {po.client}
            </div>
            {po.address && <div className="client-address">{po.address}</div>}
            {po.carts && po.carts.length > 0 && (
              <ul>
                {po.carts
                  .filter(cart => cart.status === 'in_use')
                  .map((cart, cartIndex) => (
                    <li key={cartIndex}>{formatCartType(cart.code)}</li>
                  ))}
              </ul>
            )}
          </div>
        ))
      ) : (
        <p>No hay pedidos pagados</p>
      )}
    </div> */}
  </div>
// </div>

  );
}

export default Screen;
